import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {Subscription} from 'rxjs';
import {ReviewService} from 'src/app/services/review.service';
import {LoaderService} from '../../../services/loader.service';
import {Review} from '../../../types/Review';
import {ModalsControlService} from '../../../services/modals-control.service';
import {ModalCommand} from '../../../types/ModalCommand';
import {ModalTypeEnum} from '../../../enums/ModalEnums';
import {InfoModalContent} from '../../../types/InfoModalContent';
import {finalize} from 'rxjs/operators';
import {CareproviderHelper} from '../../../services/careprovider.helper';
import {ReviewAnswer} from '../../../types/ReviewAnswer';

import * as dayjs from 'dayjs';
import 'dayjs/locale/sv';
import {LoaderQueueItem} from '../../../types/LoaderQueueItem';

@Component({
  selector: 'app-customer-rate',
  templateUrl: './customer-rate.component.html',
  styleUrls: ['./customer-rate.component.scss'],
})
export class CustomerRateComponent implements OnInit, OnDestroy {
  userDataForm: UntypedFormGroup;
  formHasError = false;
  maxCommentLength: number = 600;
  public review: Review = null;
  public customerName: string;
  private subscriptions: Subscription[] = [];
  private reviewRatingKey: string;

  constructor(
    public dialogRef: MatDialogRef<CustomerRateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: [string, string],
    private reviewService: ReviewService,
    private formBuilder: UntypedFormBuilder,
    private loaderService: LoaderService,
    private modalControl: ModalsControlService,
    private careProviderHelper: CareproviderHelper
  ) {
    dayjs.locale('sv');
  }

  ngOnInit(): void {
    this.reviewRatingKey = this.data[1];
    this.customerName = this.data[0];

    this.getReviewByKey();
    this.buildUserDataForm();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  saveRating(): void {
    (Object as any).values(this.userDataForm.controls).forEach((control) => control.markAllAsTouched());
    if (this.userDataForm.valid) {
      this.formHasError = false;
      this.updateReviewObjectFromFormData();
      const queryId: number = this.loaderService.generateQueueId();
      const queryItem: LoaderQueueItem = {
        id: queryId,
        title: '',
        description: '',
      };
      this.loaderService.addToQueue(queryItem);
      this.subscriptions.push(
        this.reviewService
          .AddCustomerReviewCommentByKey(this.reviewRatingKey, this.review)
          .pipe(
            finalize(() => {
              this.loaderService.removeFromQueue(queryId);
            })
          )
          .subscribe(
            (_) => this.onSaveAction(),
            (error) => console.error(error)
          )
      );
    } else {
      this.formHasError = true;
    }
  }

  public trackByMethodAnswers(i: number, el: ReviewAnswer) {
    return el ? el.id : null;
  }

  public formatDate(date: string): string {
    return dayjs(date).format('D MMM, YYYY');
  }

  private buildUserDataForm(): void {
    this.userDataForm = this.formBuilder.group({
      comment: ['', [Validators.required]],
    });
  }

  private getReviewByKey(): void {
    const queryId: number = this.loaderService.generateQueueId();
    const queryItem: LoaderQueueItem = {
      id: queryId,
      title: '',
      description: '',
    };
    this.loaderService.addToQueue(queryItem);
    this.subscriptions.push(
      this.reviewService
        .GetCustomerReviewByKey(this.reviewRatingKey)
        .pipe(
          finalize(() => {
            this.loaderService.removeFromQueue(queryId);
          })
        )
        .subscribe((result) => this.processReviewData(result))
    );
  }

  private processReviewData(result: Review): void {
    this.review = result;
  }

  private onSaveAction() {
    this.careProviderHelper.initOpenCustomerReviews();
    this.openInfoModal();
    this.closeDialog();
  }

  private openInfoModal(): void {
    const infoContent: InfoModalContent = {
      title: 'Tack för ditt svar!',
      message: `<p>Ditt svar kommer nu att publiceras på din sida <b>${this.customerName}</b>. Det kan ta lite tid innan det syns.</p>`,
    };
    const modalCommand: ModalCommand = {
      type: ModalTypeEnum.INFO,
      data: infoContent,
    };
    this.modalControl.openModal(modalCommand);
  }

  private updateReviewObjectFromFormData(): void {
    this.review.comment = this.userDataForm.value.comment;
  }
}
